<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useTherapistStore } from "@/stores/therapist";
import { useLocationStore } from "@/stores/location";
import LocationsDetailsPage from "./LocationsDetailsPage.vue";

const props = defineProps({
    therapistId: {
        type: String,
        required: true
    }
});

const store = useTherapistStore();
const locationStore = useLocationStore();
const router = useRouter();
const dropCache = ref(false);
const loadingTherapists = ref(true);
const loadingSyncCalendar = ref(false);

const therapist = computed(() =>
    store.therapists.find((t) => t.ROWID === props.therapistId)
);

onMounted(async () => {
    if (!store.therapists.length) {
        loadingTherapists.value = true;
        await store.fetchTherapists();
        loadingTherapists.value = false;
    } else {
        loadingTherapists.value = false;
    }

    if (!therapist.value) {
        router.push("/admin/therapists");
        return;
    }

    await locationStore.fetchTherapistLocations(therapist.value.ROWID);
});

const openLocationId = ref(null);

function handleLocationSelect(location) {
    openLocationId.value = openLocationId.value === location.ROWID ? null : location.ROWID;
    locationStore.setSelectedLocation(location);
    router.push({
        name: "therapist-details",
        params: { therapistId: props.therapistId },
        query: { location_id: location.ROWID }
    });
}

function backToTherapistsPage() {
    return router.push("/admin/therapists");
}

function updateLocation() {
    locationStore.fetchTherapistLocations();
}

async function syncCalendar() {
    loadingSyncCalendar.value = true;
    await store.syncCalendar(props.therapistId);
    loadingSyncCalendar.value = false;
}

</script>

<template>
  <div>
    <!-- Go Back Button -->
    <button @click="backToTherapistsPage()" class="mb-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
      ← Go Back
    </button>

    <!-- Loader for Therapists -->
    <div v-if="loadingTherapists" class="flex items-center justify-center py-10">
      <span class="animate-pulse text-gray-400 text-lg">Loading therapist info...</span>
    </div>

    <template v-else>
      <div v-if="therapist">
        <!-- Therapist Info Card -->
        <div class="bg-white rounded-lg shadow">
          <div class="p-6 border-b flex justify-between items-start">
            <div>
              <h2 class="text-2xl font-bold">
                {{ therapist.first_name }} {{ therapist.last_name }}
              </h2>
              <p class="text-gray-500">{{ therapist.email }}</p>
            </div>
            <div class="flex gap-2">
              <a
                :href="`https://crm.zoho.com/crm/org805341821/tab/CustomModule2/${therapist.zoho_crm_id}`"
                target="_blank"
                class="mb-4 px-4 py-2 bg-blue-500 text-nowrap text-white rounded hover:bg-blue-600"
              >
                Open in CRM
              </a>
              <button
                  @click="syncCalendar()"
                  class="mb-4 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  :disabled="loadingSyncCalendar"
              >
                {{ loadingSyncCalendar ? "Waiting..." : "Sync calendar" }}
              </button>
            </div>
          </div>
          <div class="p-6 grid grid-cols-2 gap-6 border-b">
            <div>
              <h3 class="font-semibold mb-2">Contact Details</h3>
              <div class="space-y-2 text-sm">
                <p><span class="text-gray-500">Email:</span> {{ therapist.email }}</p>
              </div>
            </div>
            <div>
              <h3 class="font-semibold mb-2">Session Details</h3>
              <div class="space-y-2 text-sm">
                <p><span class="text-gray-500">Session Time:</span> {{ therapist.session_time }} minutes</p>
                <p><span class="text-gray-500">Max Sessions:</span> {{ therapist.max_sessions_per_day }} per day</p>
                <p><span class="text-gray-500">Max Sessions:</span> {{ therapist.max_sessions_per_week }} per week</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Locations List -->
        <div class="bg-white rounded-lg mt-4 shadow">
          <div class="p-3 border-b flex items-center justify-between">
            <h3 class="font-semibold">Locations</h3>
            <div class="text-xs text-gray-400 flex flex-col">
              <span>{{ locationStore.locations.length }} found</span>
              <span @click="updateLocation()" class="hover:cursor-pointer select-none hover:underline">Update</span>
            </div>
          </div>
          <div class="p-2">
            <div v-if="locationStore.loading" class="text-gray-400">Loading locations...</div>
            <div v-else>
              <template v-if="locationStore.locations.length">
                <div
                  v-for="location in locationStore.locations"
                  :key="location.ROWID"
                  class="mb-2"
                >
                  <div
                    @click="handleLocationSelect(location)"
                    :class="[
                      'cursor-pointer p-3 border flex items-center transition-all',
                      openLocationId === location.ROWID
                        ? 'bg-blue-50 border-blue-500 rounded-t-lg hover:bg-blue-100'
                        : 'hover:bg-gray-50 rounded-lg border-gray-200 shadow'
                    ]"
                  >
                    <div class="mr-4 select-none flex-shrink-0">
                      <span v-if="location.name === 'Online'" class="text-blue-500 text-xl" title="Online">💻</span>
                      <span v-else class="text-green-500 text-xl" title="In Person">🏢</span>
                    </div>
                    <div class="flex-1">
                      <div class="font-semibold select-none text-lg">{{ location.name }}</div>
                      <div class="text-xs select-none text-gray-500">{{ location.timezone }}</div>
                      <div v-if="location.address" class="text-sm text-gray-600 mt-1">
                        <span>{{ location.address }}</span>
                        <a
                          v-if="location.latitude && location.longitude"
                          :href="`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`"
                          target="_blank"
                          class="ml-2 text-blue-500 hover:underline"
                          title="View on Google Maps"
                        >🗺️ Map</a>
                      </div>
                    </div>
                    <div v-if="openLocationId === location.ROWID" class="ml-4 text-nowrap select-none text-blue-600 font-semibold">Hide Details ▲</div>
                    <div v-else class="ml-4 text-nowrap select-none text-gray-400">Show Details ▼</div>
                  </div>
                  <LocationsDetailsPage
                    class="rounded-b-lg"
                    v-if="openLocationId === location.ROWID"
                    :location="location"
                    :therapist-id="therapist.ROWID"
                    :drop-cache="dropCache"
                  />
                </div>
              </template>
              <template v-else>
                <div class="text-gray-400 text-center py-6">No locations available for this therapist.</div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-gray-400 text-center py-10">
        Therapist not found.
      </div>
    </template>
  </div>
</template>
